import { create } from 'zustand';
import { getApiErrorMessage, heldOrdersAPI } from '@matix/api-client';
import { trNow } from '../lib/language';
import { heldErrorText } from '../features/held-orders/heldEdit';
import type { HeldOrderRow } from '@matix/types';

/**
 * Held invoices — Inv_Receipt_h.IsHolded = 1, the legacy till's "Holded
 * Orders". The server is the only truth: unlike the old local hold there is
 * no snapshot on the device, because the invoice itself is the record.
 */
interface HeldOrdersState {
  rows: HeldOrderRow[];
  loading: boolean;
  error: string | null;
  /** `mine` or, with "View All Branch Holded Orders", the whole branch. */
  scope: 'mine' | 'branch';
  canViewBranch: boolean;
  setScope: (scope: 'mine' | 'branch') => void;
  refresh: () => Promise<void>;
  /**
   * Cancel a held invoice with the MANAGER's password (LoginName 'Admin') —
   * no Cancel Order permission needed. Its table frees itself. Resolves to
   * the message to show.
   */
  cancel: (
    id: number,
    managerPassword: string,
    cancelReasonId: number | null,
    note: string | null,
  ) => Promise<{ ok: boolean; message: string }>;
}

export const useHeldOrders = create<HeldOrdersState>((set, get) => ({
  rows: [],
  loading: false,
  error: null,
  scope: 'mine',
  canViewBranch: false,

  setScope: (scope) => {
    set({ scope });
    void get().refresh();
  },

  refresh: async () => {
    set({ loading: true });
    try {
      const r = await heldOrdersAPI.list(get().scope);
      set({ rows: r.data, scope: r.scope, canViewBranch: r.can_view_branch, error: null });
    } catch (err) {
      set({ error: getApiErrorMessage(err) || 'Could not load held orders' });
    } finally {
      set({ loading: false });
    }
  },

  cancel: async (id, managerPassword, cancelReasonId, note) => {
    try {
      const result = await heldOrdersAPI.cancel(id, {
        manager_password: managerPassword,
        cancel_reason_id: cancelReasonId,
        note,
      });
      await get().refresh();
      return {
        ok: true,
        message: trNow({
          en: `Order #${result.entry_no} cancelled`,
          ar: `تم إلغاء الطلب #${result.entry_no}`,
        }),
      };
    } catch (err) {
      return {
        ok: false,
        message:
          heldErrorText(err) ||
          getApiErrorMessage(err) ||
          trNow({ en: 'Could not cancel this order', ar: 'تعذّر إلغاء هذا الطلب' }),
      };
    }
  },
}));
